/**
 * /trust/sandbox UI — six live Graph proof panels.
 *
 * Customer-facing: every panel renders the literal TypeScript snippet that
 * Repancy runs, then the live result. Trust by transparency.
 */

import { useMemo, useState } from 'react';
import { useConvex } from 'convex/react';
import { useUser } from '@clerk/react';
import {
  makeSandbox,
  SandboxOutOfScopeError,
  type RoundTripStep,
  type SandboxApi,
} from '../../lib/graph/sandbox';
import {
  makeConvexAuditWriter,
} from '../../lib/graph/sandboxWiring';
import { getActiveAccount } from '../../lib/graph/msalClient';
import { api } from '../../../convex/_generated/api';

function useSandbox(): SandboxApi {
  const convex = useConvex();
  return useMemo(
    () =>
      makeSandbox({
        audit: makeConvexAuditWriter(
          convex,
          false,
          undefined,
          api,
        ),
        isDemo: false,
      }),
    [convex],
  );
}

export function SandboxPanel() {
  const { isSignedIn } = useUser();
  const sandbox = useSandbox();

  if (!isSignedIn) {
    return (
      <p className="text-black leading-relaxed">
        Sign in to run live Microsoft Graph operations against your own tenant.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-[720px] text-black leading-relaxed">
        Six safe Microsoft Graph operations you can run against your own
        tenant to verify Repancy's integration is real. Every operation is
        scoped to a tight allow-list of read-only paths; the round-trip
        writes a single throwaway file to your OneDrive root and deletes it.
      </p>

      <Panel1Me sandbox={sandbox} />
      <Panel2Groups sandbox={sandbox} />
      <Panel3Sites sandbox={sandbox} />
      <Panel4Permissions sandbox={sandbox} />
      <Panel5DryRunLink sandbox={sandbox} />
      <Panel6RoundTrip sandbox={sandbox} />
    </div>
  );
}

interface PanelProps {
  sandbox: SandboxApi;
}

function Card({
  title,
  children,
  snippet,
}: {
  title: string;
  children: React.ReactNode;
  snippet?: string;
}) {
  return (
    <section className="rounded border border-black/10 bg-white">
      <header className="flex items-center gap-2 border-b border-black/10 px-4 py-2.5">
        <h3 className="m-0 text-sm font-bold text-black">{title}</h3>
      </header>
      {snippet && (
        <pre className="m-0 overflow-x-auto border-b border-black/10 bg-[#0a0a14] p-3 text-[11px] text-[#9fdfff] font-mono">
          {snippet}
        </pre>
      )}
      <div className="p-4 text-[13px] text-black">{children}</div>
    </section>
  );
}

function RunButton({
  loading,
  onClick,
  disabled,
  children,
  testId,
}: {
  loading?: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <button
      type="button"
      disabled={loading || disabled}
      onClick={onClick}
      data-testid={testId}
      className={`rounded border border-[var(--xp-selection)] px-3 py-1 text-xs font-semibold ${
        loading || disabled
          ? 'cursor-default bg-gray-200 text-gray-500'
          : 'cursor-pointer bg-[#d0d0d0] text-black hover:bg-[#e0e0e0]'
      }`}
    >
      {loading ? 'Running…' : children}
    </button>
  );
}

function ResultBox({
  value,
  error,
  testId,
}: {
  value?: unknown;
  error?: string;
  testId: string;
}) {
  if (error) {
    return (
      <pre
        data-testid={testId}
        className="mt-2 overflow-x-auto rounded border border-[#cc0000] bg-[#fff5f5] p-2 text-[11px] text-[#cc0000] font-mono"
      >
        {error}
      </pre>
    );
  }
  if (value === undefined) return null;
  return (
    <pre
      data-testid={testId}
      className="mt-2 max-h-[260px] overflow-auto rounded border border-black/10 bg-[#f7f7f7] p-2 text-[11px] text-black font-mono"
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

// === Panel 1 — Who am I ===

function Panel1Me({ sandbox }: PanelProps) {
  const [result, setResult] = useState<unknown>();
  const [err, setErr] = useState<string>();
  const [loading, setLoading] = useState(false);
  return (
    <Card
      title="Panel 1 — Who am I?"
      snippet={`const me = await getGraphClient().api('/me').get();`}
    >
      <p className="m-0 mb-2 text-black">Returns your Entra ID profile. Reads only, never modifies anything.</p>
      <RunButton
        loading={loading}
        testId="sandbox-panel-1-run"
        onClick={async () => {
          setErr(undefined);
          setResult(undefined);
          setLoading(true);
          try {
            if (!getActiveAccount()) {
              throw new Error('Connect a Microsoft account first (Settings → General).');
            }
            setResult(await sandbox.getMe());
          } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
          } finally {
            setLoading(false);
          }
        }}
      >
        Run
      </RunButton>
      <ResultBox value={result} error={err} testId="sandbox-panel-1-result" />
    </Card>
  );
}

// === Panel 2 — Groups ===

function Panel2Groups({ sandbox }: PanelProps) {
  const [result, setResult] = useState<unknown>();
  const [err, setErr] = useState<string>();
  const [loading, setLoading] = useState(false);
  return (
    <Card
      title="Panel 2 — What Entra ID groups am I in?"
      snippet={`const groups = await getGraphClient().api('/me/memberOf?$top=20').get();`}
    >
      <p className="m-0 mb-2 text-black">Lists your directory group memberships + assigned admin roles.</p>
      <RunButton
        loading={loading}
        testId="sandbox-panel-2-run"
        onClick={async () => {
          setErr(undefined);
          setResult(undefined);
          setLoading(true);
          try {
            if (!getActiveAccount()) {
              throw new Error('Connect a Microsoft account first.');
            }
            setResult(await sandbox.getMyGroups());
          } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
          } finally {
            setLoading(false);
          }
        }}
      >
        Run
      </RunButton>
      <ResultBox value={result} error={err} testId="sandbox-panel-2-result" />
    </Card>
  );
}

// === Panel 3 — Sites ===

function Panel3Sites({ sandbox }: PanelProps) {
  const [term, setTerm] = useState('sharepoint');
  const [result, setResult] = useState<unknown>();
  const [err, setErr] = useState<string>();
  const [loading, setLoading] = useState(false);

  return (
    <Card
      title="Panel 3 — What SharePoint sites can I see?"
      snippet={`const sites = await getGraphClient()\n  .api('/sites?search=' + encodeURIComponent(query)).get();`}
    >
      <p className="m-0 mb-2 text-black">
        Microsoft Graph requires a search term (rejects "*"/empty). Default is "sharepoint" which matches anything.
      </p>
      <div className="mb-2 flex items-center gap-2">
        <label className="text-xs text-black">Search term:</label>
        <input
          type="text"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          data-testid="sandbox-panel-3-term"
          className="rounded border border-black/30 px-2 py-1 text-xs text-black"
        />
      </div>
      <RunButton
        loading={loading}
        testId="sandbox-panel-3-run"
        onClick={async () => {
          setErr(undefined);
          setResult(undefined);
          setLoading(true);
          try {
            if (!getActiveAccount()) {
              throw new Error('Connect a Microsoft account first.');
            }
            setResult(await sandbox.searchSites(term));
          } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
          } finally {
            setLoading(false);
          }
        }}
      >
        Run
      </RunButton>
      <ResultBox value={result} error={err} testId="sandbox-panel-3-result" />
    </Card>
  );
}

// === Panel 4 — File permissions ===

function Panel4Permissions({ sandbox }: PanelProps) {
  const [driveId, setDriveId] = useState('');
  const [itemId, setItemId] = useState('');
  const [result, setResult] = useState<unknown>();
  const [err, setErr] = useState<string>();
  const [loading, setLoading] = useState(false);
  return (
    <Card
      title="Panel 4 — Who has access to a file?"
      snippet={`const perms = await getGraphClient()\n  .api(\`/drives/\${driveId}/items/\${itemId}/permissions\`).get();`}
    >
      <p className="m-0 mb-2 text-black">
        Paste a driveId and itemId from your tenant (e.g. via Repancy's Explorer). We GET the permissions list — read only.
      </p>
      <div className="mb-2 flex flex-col gap-2">
        <input
          type="text"
          placeholder="driveId"
          value={driveId}
          onChange={(e) => setDriveId(e.target.value)}
          data-testid="sandbox-panel-4-drive"
          className="rounded border border-black/30 px-2 py-1 text-xs text-black"
        />
        <input
          type="text"
          placeholder="itemId"
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          data-testid="sandbox-panel-4-item"
          className="rounded border border-black/30 px-2 py-1 text-xs text-black"
        />
      </div>
      <RunButton
        loading={loading}
        disabled={!driveId || !itemId}
        testId="sandbox-panel-4-run"
        onClick={async () => {
          setErr(undefined);
          setResult(undefined);
          setLoading(true);
          try {
            if (!getActiveAccount()) {
              throw new Error('Connect a Microsoft account first.');
            }
            setResult(await sandbox.getItemPermissions(driveId, itemId));
          } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
          } finally {
            setLoading(false);
          }
        }}
      >
        Run
      </RunButton>
      <ResultBox value={result} error={err} testId="sandbox-panel-4-result" />
    </Card>
  );
}

// === Panel 5 — Dry-run share link ===

function Panel5DryRunLink({ sandbox }: PanelProps) {
  const [driveId, setDriveId] = useState('');
  const [itemId, setItemId] = useState('');
  const [result, setResult] = useState<unknown>();
  const [loading, setLoading] = useState(false);
  return (
    <Card
      title="Panel 5 — Dry-run share-link creation"
      snippet={`// We never actually POST. This is the body Repancy WOULD send:\nconst body = await dryRunCreateLink({ driveId, itemId, type: 'view', scope: 'organization' });`}
    >
      <p className="m-0 mb-2 text-black">
        Inspect the exact request body Repancy would send if you asked it to create a share link. Nothing is created in your tenant.
      </p>
      <div className="mb-2 flex flex-col gap-2">
        <input
          type="text"
          placeholder="driveId"
          value={driveId}
          onChange={(e) => setDriveId(e.target.value)}
          data-testid="sandbox-panel-5-drive"
          className="rounded border border-black/30 px-2 py-1 text-xs text-black"
        />
        <input
          type="text"
          placeholder="itemId"
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          data-testid="sandbox-panel-5-item"
          className="rounded border border-black/30 px-2 py-1 text-xs text-black"
        />
      </div>
      <RunButton
        loading={loading}
        disabled={!driveId || !itemId}
        testId="sandbox-panel-5-run"
        onClick={async () => {
          setLoading(true);
          try {
            setResult(
              await sandbox.dryRunCreateLink({
                driveId,
                itemId,
                type: 'view',
                scope: 'organization',
              }),
            );
          } finally {
            setLoading(false);
          }
        }}
      >
        Preview body
      </RunButton>
      <ResultBox value={result} testId="sandbox-panel-5-result" />
    </Card>
  );
}

// === Panel 6 — Round trip ===

function Panel6RoundTrip({ sandbox }: PanelProps) {
  const { user } = useUser();
  const [steps, setSteps] = useState<RoundTripStep[]>([]);
  const [loading, setLoading] = useState(false);

  return (
    <Card
      title="Panel 6 — OneDrive round-trip (create → read → delete a scratch file)"
      snippet={`await runOneDriveRoundTrip({ userId, onStep: (s) => setSteps(...) });\n// Writes to /me/drive/root:/repancy-sandbox-{userId}-{ts}.md only.\n// Path allow-list rejects every other write.`}
    >
      <p className="m-0 mb-2 text-black">
        Creates a tiny markdown file in your OneDrive root, reads it back to confirm the content, and deletes it. Five steps with green checkmarks.
      </p>
      <RunButton
        loading={loading}
        testId="sandbox-panel-6-run"
        onClick={async () => {
          setSteps([]);
          setLoading(true);
          try {
            if (!getActiveAccount()) {
              throw new Error('Connect a Microsoft account first.');
            }
            const userId = user?.id ?? 'anon';
            const { steps: result } = await sandbox.runOneDriveRoundTrip({
              userId,
              onStep: (_, all) => setSteps(all),
            });
            setSteps(result);
          } catch (e) {
            const msg = e instanceof SandboxOutOfScopeError
              ? `Out of sandbox scope: ${e.message}`
              : (e instanceof Error ? e.message : String(e));
            setSteps([{ label: 'Round trip', status: 'fail', detail: msg }]);
          } finally {
            setLoading(false);
          }
        }}
      >
        Run the round-trip test
      </RunButton>
      {steps.length > 0 && (
        <ol
          data-testid="sandbox-panel-6-result"
          className="mt-2 ml-0 list-decimal pl-5"
        >
          {steps.map((s, i) => (
            <li
              key={i}
              data-testid={`sandbox-panel-6-step-${i + 1}`}
              className="my-1 text-[13px] text-black"
            >
              <StatusDot status={s.status} /> <strong>{s.label}</strong>
              {s.detail && <span className="ml-2 text-black">— {s.detail}</span>}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function StatusDot({ status }: { status: RoundTripStep['status'] }) {
  const cls =
    status === 'ok' ? 'text-[#008000]'
    : status === 'fail' ? 'text-[#cc0000]'
    : 'text-[#888]';
  const glyph = status === 'ok' ? '✓' : status === 'fail' ? '✗' : '…';
  return <span className={`inline-block w-4 ${cls} font-bold`}>{glyph}</span>;
}
